import React, { useCallback, useEffect, useState, useRef, Suspense, lazy } from 'react';
import axios from 'axios';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, HashRouter, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store, { RootState } from './store/store';
import { queryClient } from './infrastructure/react-query/queryClient';
import { decodeJwtPayload } from './utils/jwt';
import { ESPELHO } from './espelho/ativo';

import Home from './views/home/pages/home';
import TelaoPage from './views/telao/pages/TelaoPage';
import Recepcao from './views/recepcao/pages/recepcao';
import ChefeDeOficina from './views/chefe_de_oficina/pages/ChefeDeOficina';
import PartLogs from './views/parts_logs/pages/PartLogs';
import ControleProducao from './views/controle_producao/pages/ControleProducao';
import TelaDeControle from './views/tela_de_controle/pages/telaDeControle';
import AuditoriaPage from './views/auditoria/pages/AuditoriaPage';
import Administracao from './views/administracao/pages/Administracao';
import RastreamentoPage from './views/rastreamento/pages/RastreamentoPage';
import AndamentoVeiculosPage from './views/setor_eventos/pages/AndamentoVeiculosPage';
import { lerTokenDoCookie } from './shared/cookie-da-sessao';
// at-114 (ADR 0059): Oficina Externa — as telas do inspetor (offline) e a leitura do Chefe.
import OficinaExternaArvorePage from './views/oficina_externa/pages/OficinaExternaArvorePage';
import OficinaExternaFilaPage from './views/oficina_externa/pages/OficinaExternaFilaPage';
import OficinasExternasLeituraPage from './views/oficina_externa/pages/OficinasExternasLeituraPage';
import OficinaExternaConfigPage from './views/admin-sistema/pages/OficinaExternaConfigPage';
import {
  OFICINA_EXTERNA,
  OFICINA_EXTERNA_FILA,
  OFICINAS_EXTERNAS,
} from './views/oficina_externa/routes';
import DashboardAssociacaoPage from './views/contac/dashboard_associacao/pages/DashboardAssociacaoPage';
import RadarFinanceiroPage from './views/contac/radar/pages/RadarFinanceiroPage';
import DashboardOficinaPage from './views/dashboard_oficina/pages/DashboardOficinaPage';
import DashboardComprasPage from './views/dashboard_compras/pages/DashboardComprasPage';
import ContacDashboard from './views/contac/dashboard/pages/DashboardCategoria';
import ContacDashboardConsultor from './views/contac/dashboard-consultor/pages/DashboardConsultor';
import DashboardRegional from './views/contac/dashboard-regional/pages/DashboardRegional';
// [FUTURE-REFATORACAO: REMOVE_COOPERATIVAS]
// import DashboardCooperativa from './views/contac/dashboard-cooperativa/pages/DashboardCooperativa';
import ResultadoPorPlaca from './views/contac/resultado_por_placa/pages/resultadoPorPlaca';
// import RelatorioPage from './views/contac/relatorio/pages/relatorio';

import PrivateRoute from './components/routes/PrivateRoute';
import RoleGuard from './components/routes/RoleGuard';
import { modulosNovosSomenteAdmin, papeisModuloNovo } from './shared/modulos-novos';
import RedirectByRole from './components/routes/RedirectByRole';
import LoginPage from './views/auth/pages/login';
import ChangePasswordPage from './views/auth/pages/changePassword';
import ForgotPasswordPage from './views/auth/pages/forgotPassword';
import GoogleCallback from './views/auth/pages/GoogleCallback';
import ChecklistPublicoPage from './views/checklist_publico/ChecklistPublicoPage';
import PesquisaPublicaPage from './views/pesquisa_publica/PesquisaPublicaPage';
import PesquisaTotemPage from './views/pesquisa_totem/PesquisaTotemPage';
import PortalCotacoesList from './views/portal_cotacoes/pages/PortalCotacoesList';
import PortalCotacaoDetalhe from './views/portal_cotacoes/pages/PortalCotacaoDetalhe';
import PortalCotacoesAprovacoes from './views/portal_cotacoes/pages/PortalCotacoesAprovacoes';
import PortalCotacoesSupervisao from './views/portal_cotacoes/pages/PortalCotacoesSupervisao';
import PortalCotacoesConfig from './views/portal_cotacoes/pages/PortalCotacoesConfig';
// at-86: Estoque — a fila de chegada das peças compradas pelo Portal.
import ModalTrocaSenhaPrimeiroAcesso from './components/modal/auth/ModalTrocaSenhaPrimeiroAcesso';
import UsersTablePage from './views/user/pages/usersTable';
import Sidebar from './components/sidebar/Sidebar';
import ChecklistHistoryPage from './views/checklist/pages/ChecklistHistoryPage';
import FormulariosPage from './views/formularios/pages/FormulariosPage';
import AdministracaoSistemaPage from './views/admin-sistema/pages/AdministracaoSistemaPage';
import ConsumoIaPage from './views/admin-sistema/pages/ConsumoIaPage';
import LogsDoSistemaPage from './views/admin-sistema/pages/LogsDoSistemaPage';
import { obterTemaDaAplicacao } from './api/aparenciaApi';
import { definirTemaConfigurado, forcarClaro } from './shared/tema/tema';
import { notify } from './shared/feedback/notify';
import LogoSoac from './components/logo-soac/LogoSoac';
import SinoDoTopo from './components/sino-notificacoes/SinoDoTopo';
import RadarAdminPage from './views/admin-sistema/pages/RadarAdminPage';
import ComprasConfigPage from './views/admin-sistema/pages/ComprasConfigPage';
import GruposDeAcessoPage from './views/admin-sistema/pages/GruposDeAcessoPage';
import ComissionamentosConfigPage from './views/admin-sistema/pages/ComissionamentosConfigPage';
import ContacConfigPage from './views/admin-sistema/pages/ContacConfigPage';
import OrcamentoCiliaList from './views/orcamento_cilia/pages/OrcamentoCiliaList';
import OrcamentoCiliaDetail from './views/orcamento_cilia/pages/OrcamentoCiliaDetail';
import OrcamentoCiliaConfig from './views/orcamento_cilia/pages/OrcamentoCiliaConfig';
import AdminSistemaHub from './views/admin-sistema/pages/AdminSistemaHub';
import IntegracoesPage from './views/admin-sistema/pages/IntegracoesPage';
import { PORTAL_COTACOES_HABILITADO } from './api/portalCotacoes/portalCotacoesApi';
import { ORCAMENTO_CILIA_HABILITADO } from './api/orcamentoCiliaApi';
import { BANCO_SYNC_HABILITADO } from './api/bancoSync/bancoSyncApi';
import SincronizacaoBasePage from './views/sincronizacao_base/pages/SincronizacaoBasePage';
import { FIPE_SYNC_HABILITADO } from './api/fipeSync/fipeSyncApi';
import PrevisaoPecasPage from './views/previsao_pecas/pages/PrevisaoPecasPage';
import PedidoPage from './views/previsao_pecas/pages/PedidoPage';
import { PREVISAO_PECAS, PREVISAO_PECAS_PEDIDO } from './views/previsao_pecas/routes';
import SincronizacaoFipePage from './views/sincronizacao_fipe/pages/SincronizacaoFipePage';

import { useCarStatusSocket } from './hooks/useCarStatusSocket';
import { usePortalCotacoesSocket } from './hooks/usePortalCotacoesSocket';
import { useOrcamentoCiliaSocket } from './hooks/useOrcamentoCiliaSocket';

import { HOME } from './views/home/routes';
import { TELAO } from './views/telao/routes';
import { LOGIN, CHANGE_PASSWORD, FORGOT_PASSWORD } from './views/auth/routes';
import { USERS_TABLE } from './views/user/routes';
import { RASTREAMENTO } from './views/rastreamento/routes';
import { ANDAMENTO_VEICULOS } from './views/setor_eventos/routes';
import { DASHBOARD_ASSOCIACAO } from './views/contac/dashboard_associacao/routes';
import { RADAR_FINANCEIRO } from './views/contac/radar/routes';
import { DASHBOARD_OFICINA } from './views/dashboard_oficina/routes';
import { DASHBOARD_COMPRAS } from './views/dashboard_compras/routes';
import { RECEPCAO } from './views/recepcao/routes';
import {
  CHEFE_DE_OFICINA,
} from './views/chefe_de_oficina/routes';
import { PARTS_LOGS } from './views/parts_logs/routes';
import { CONTROLE_PRODUCAO } from './views/controle_producao/routes';
import { TELA_DE_CONTROLE } from './views/tela_de_controle/routes';
import { AUDITORIA } from './views/auditoria/routes';
import { CONTAC_DASHBOARD } from './views/contac/dashboard/routes';
import { RESULTADO_POR_PLACA } from './views/contac/resultado_por_placa/routes';
// Rota desativada junto com o import do componente (ver `RelatorioPage`, comentado acima).
// import { CONTAC_RELATORIO } from './views/contac/relatorio/routes';

import { CONTAC_DASHBOARD_CONSULTOR } from './views/contac/dashboard-consultor/routes';
import { CONTAC_DASHBOARD_REGIONAL } from './views/contac/dashboard-regional/routes';
// [FUTURE-REFATORACAO: REMOVE_COOPERATIVAS]
// import { CONTAC_DASHBOARD_COOPERATIVA } from './views/contac/dashboard-cooperativa/routes';
import { FORMULARIOS } from './views/formularios/routes';
import { ADMINISTRACAO } from './views/administracao/routes';
import {
  ADMIN_SISTEMA,
  ADMIN_SISTEMA_CILIA,
  ADMIN_SISTEMA_COMPRAS,
  ADMIN_SISTEMA_CONSUMO_IA,
  ADMIN_SISTEMA_LOGS,
  ADMIN_SISTEMA_GRUPOS,
  ADMIN_SISTEMA_COMISSIONAMENTOS,
  ADMIN_SISTEMA_CONTAC,
  ADMIN_SISTEMA_COTACOES,
  ADMIN_SISTEMA_INTEGRACOES,
  ADMIN_SISTEMA_OFICINA,
  ADMIN_SISTEMA_OFICINA_EXTERNA,
  ADMIN_SISTEMA_RADAR
} from './views/admin-sistema/routes';
import {
  ORCAMENTOS_CILIA,
  ORCAMENTOS_CILIA_DETAIL
} from './views/orcamento_cilia/routes';

import Comissionamentos from './views/comissionamentos/pages/comissionamentos';
import RegrasPage from './views/comissionamentos/pages/regras';
import ComissionamentosHome from './views/comissionamentos/pages/home';
import PagamentosPage from './views/comissionamentos/pages/pagamentos';
import ContratosPage from './views/comissionamentos/pages/contratos';
import MovimentacoesRetroativasPage from './views/comissionamentos/pages/movimentacoes';

import {
  COMISSIONAMENTOS,
  COMISSIONAMENTOS_REGRAS,
  COMISSIONAMENTOS_HOME,
  COMISSIONAMENTOS_LISTAS,
  COMISSIONAMENTOS_PAGAMENTOS,
  COMISSIONAMENTOS_CONTRATOS,
  COMISSIONAMENTOS_MOVIMENTACOES,
} from './views/comissionamentos/routes';

import {
  setAccessToken,
  clearAccessToken,
  startRefreshingToken,
  setMustChangePassword,
  setUserType,
  setCodigoVoluntario,
} from './views/auth/reducer';

// Tela pública do fornecedor: carregada sob demanda (ADR 0002 do Portal de
// Cotações) — o fornecedor abre o link no celular, muitas vezes em 4G ruim, e
// não pode baixar o bundle inteiro do sistema interno para preencher um formulário.
// O espelho (transversal/at-87) é publicado como página estática: sem servidor que devolva o
// index em qualquer caminho, a rota vive no hash.
const Router = ESPELHO ? HashRouter : BrowserRouter;

const CotacaoPublicaPage = lazy(() => import('./views/cotacao_publica/CotacaoPublicaPage'));

const App: React.FC = () => {
  const dispatch = useDispatch();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  useCarStatusSocket();
  /*
   * Conexão do Portal de Cotações (ADR 0005 do módulo). Vive aqui, e não na tela do
   * módulo, porque o badge do menu precisa dela em QUALQUER tela — o hook tem gate próprio
   * (env do módulo + papel), então quem não usa o Portal não abre conexão.
   */
  usePortalCotacoesSocket();
  /*
   * Conexão do Orçamento Cilia (ADR 0003 do módulo). Mesma razão de morar aqui: o badge do
   * menu precisa dela em qualquer tela, e o hook tem gate próprio (env + papel).
   */
  useOrcamentoCiliaSocket();
  const mustChangePassword = useSelector((state: RootState) => state.auth.mustChangePassword);
  const location = useLocation();

  const [isPasswordChangeDeferred, setIsPasswordChangeDeferred] = useState<boolean>(
    () => sessionStorage.getItem('passwordChangeDeferred') === 'true'
  );

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const storedSidebarOpen = localStorage.getItem('sidebarOpen');
    return storedSidebarOpen !== null ? storedSidebarOpen === 'true' : true;
  });

  const sidebarRef = useRef<HTMLDivElement>(null);
  const isRefreshingRef = useRef(false);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem('sidebarOpen', String(next));
      return next;
    });
  };

  const shouldRenderSidebar =
    ![
      LOGIN(),
      CHANGE_PASSWORD(),
      FORGOT_PASSWORD(),
      '/auth/google/callback',
      // at-130: o Telão em tela cheia fica na TV da oficina — sem menu por cima. É rota
      // AUTENTICADA (está no RoleGuard abaixo); sair do shell não a torna pública.
      TELAO(),
    ].includes(location.pathname) &&
    // Página pública do checklist compartilhado (sem login/sidebar)
    !location.pathname.startsWith('/checklist/') &&
    // Pesquisa de satisfação do associado: quem abre não tem login, e a sidebar
    // (com "Logout" e menu interno) aparecia por cima da tela dele.
    // ⚠️ '/pesquisa/' (com barra) NÃO cobre '/pesquisa-totem' — as duas exclusões
    // são necessárias.
    !location.pathname.startsWith('/pesquisa/') &&
    // Totem da oficina (ADR 0032): dispositivo compartilhado, sem login.
    !location.pathname.startsWith('/pesquisa-totem') &&
    // Página pública do fornecedor (Portal de Cotações): quem abre não tem login —
    // a sidebar aparecia por cima do formulário (12/08/2026). Sem a barra final de
    // propósito: a própria tela troca a URL para /cotacao (replaceState) para tirar
    // o token da barra de endereço, e a exclusão precisa cobrir as duas formas.
    !location.pathname.startsWith('/cotacao');

  // at-130: o tema da aplicação vale nas telas internas e no Telão. Login e telas públicas
  // (associado, totem, fornecedor) ficam sempre claras.
  useEffect(() => {
    forcarClaro(!(shouldRenderSidebar || location.pathname === TELAO()));
  }, [shouldRenderSidebar, location.pathname]);

  // O tema é de cada usuário e vem do servidor a cada sessão; até ela chegar, vale a última guardada neste
  // navegador. Falha só avisa: a tela segue no tema conhecido, sem bloquear nada.
  useEffect(() => {
    if (!accessToken) return;
    obterTemaDaAplicacao()
      .then((r) => definirTemaConfigurado(r.tema))
      .catch(() =>
        notify.warning('Não foi possível carregar o tema da aplicação. A tela segue no último tema conhecido.', {
          toastId: 'tema-da-aplicacao',
        }),
      );
  }, [accessToken]);

  // As três funções abaixo são memoizadas em cadeia (at-39) porque o efeito de bootstrap
  // de sessão depende delas. Sem `useCallback`, elas nasceriam de novo a cada render e,
  // ao entrarem no array de dependências, o efeito recriaria o intervalo e redespacharia
  // o token em todo render — loop de render. A cadeia mantém o comportamento original:
  // o efeito só re-executa quando `accessToken` muda.
  const applyTokenToState = useCallback((token: string) => {
    dispatch(setAccessToken(token));

    const payload = decodeJwtPayload(token);
    if (payload) {
      dispatch(setUserType(payload.userType ?? null));
      dispatch(setCodigoVoluntario(payload.codigoVoluntario ?? null));
      dispatch(setMustChangePassword(Boolean(payload.mustChangePassword)));
    }
  }, [dispatch]);

  const refreshAccessToken = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      dispatch(startRefreshingToken());

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );

      const data = response.data;
      localStorage.setItem('authToken', data.token);
      applyTokenToState(data.token);
    } catch (error) {
      console.error('Erro ao atualizar o token:', error);
      localStorage.removeItem('authToken');
      dispatch(clearAccessToken());
    } finally {
      isRefreshingRef.current = false;
    }
  }, [dispatch, applyTokenToState]);

  const checkTokenExpiration = useCallback(() => {
    if (isRefreshingRef.current) return;
    if (accessToken) {
      const payload = decodeJwtPayload(accessToken);
      if (!payload?.exp) return;
      const exp = payload.exp * 1000;
      const currentTime = Date.now();

      if (exp - currentTime < 30 * 1000) {
        refreshAccessToken();
      }
    }
  }, [accessToken, refreshAccessToken]);

  useEffect(() => {
    const fetchTokenFromCookie = () => {
      const token = lerTokenDoCookie() || localStorage.getItem('authToken');

      if (token) {
        applyTokenToState(token);
      }
    };

    fetchTokenFromCookie();

    const interval = setInterval(checkTokenExpiration, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [applyTokenToState, checkTokenExpiration]);

  return (
    <div>
      <div className="app">
        {shouldRenderSidebar && (
          <>
            <div ref={sidebarRef}>
              <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
            </div>
            <div className="mobile-header-bar">
              <button
                className="mobile-sidebar-toggle-btn"
                onClick={toggleSidebar}
                aria-label="Abrir menu"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              </button>
              <div className="mobile-header-title">SOAC</div>
              <div style={{ width: '44px' }}></div>
            </div>
          </>
        )}

        <div
          className={`page-content ${
            shouldRenderSidebar ? (isSidebarOpen ? 'sidebar-open' : 'sidebar-closed') : ''
          }${shouldRenderSidebar && location.pathname !== HOME() ? ' com-logo-topo' : ''}${shouldRenderSidebar && accessToken ? ' com-sino-topo' : ''}`}
        >
          {/*
            at-130: o logo do SOAC no topo de toda tela interna, na MESMA posição do logo da Home
            e do Telão (mesmo tamanho e margens). A Home desenha o seu dentro do próprio cabeçalho,
            ao lado da busca e do relógio, por isso fica fora daqui.
          */}
          {/*
            transversal/at-90: o sino ÚNICO, fixo no canto superior direito de toda tela interna
            (Home incluída). Substituiu os sinos que viviam dentro de três telas.
          */}
          {shouldRenderSidebar && accessToken && <SinoDoTopo />}
          {shouldRenderSidebar && location.pathname !== HOME() && (
            <div className="app-topo-logo">
              <LogoSoac tamanho={44} corDoFundo="var(--tema-fundo-2, #e9e9e9)" />
            </div>
          )}
          <Routes>
            <Route
              path={HOME()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard
                      requiredRoles={[
                        'RECEPCAO',
                        'CHEFE_DE_OFICINA',
                        'COLABORADOR',
                        'ORCAMENTO',
                        'COMPRA',
                        'ESTOQUE',
                        'CONSULTOR',
                        'REGIONAL',
                        'ADMINISTRACAO',
                        'TI',
                        'TELAO',
                      ]}
                    >
                      <Home sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/* at-130: o Telão em tela cheia. Mesmos papéis da Home — é a mesma informação,
                só sem o menu. */}
            <Route
              path={TELAO()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard
                      requiredRoles={[
                        'RECEPCAO',
                        'CHEFE_DE_OFICINA',
                        'COLABORADOR',
                        'ORCAMENTO',
                        'COMPRA',
                        'ESTOQUE',
                        'CONSULTOR',
                        'REGIONAL',
                        'ADMINISTRACAO',
                        'TI',
                        'TELAO',
                      ]}
                    >
                      <TelaoPage />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route path="/" element={<PrivateRoute element={<RedirectByRole />} />} />
            <Route path="*" element={<PrivateRoute element={<RedirectByRole />} />} />

            <Route path={LOGIN()} element={<LoginPage />} />
            <Route path={CHANGE_PASSWORD()} element={<ChangePasswordPage />} />
            <Route path={FORGOT_PASSWORD()} element={<ForgotPasswordPage />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            {/* Página pública do checklist compartilhado via WhatsApp (token + telefone) */}
            <Route path="/checklist/:token" element={<ChecklistPublicoPage />} />
            {/* Pesquisa de satisfação respondida pelo associado, sem login (token + telefone) */}
            <Route path="/pesquisa/:token" element={<PesquisaPublicaPage />} />
            {/* Totem da oficina (ADR 0032): mesma pesquisa, identificação por placa + telefone */}
            <Route path="/pesquisa-totem" element={<PesquisaTotemPage />} />
            {/*
              Portal de Cotações: as rotas só são registradas quando a env do backend
              do módulo existe (PORTAL_COTACOES_HABILITADO). Sem ela — o caso do build
              de produção — nem a tela pública do fornecedor nem as internas existem.
              A configuração do módulo NÃO fica aqui: vive em /admin/sistema/cotacoes.
            */}
            {PORTAL_COTACOES_HABILITADO && (
              <>
                {/* Página pública do fornecedor — link da cotação (token + 4 dígitos do CNPJ) */}
                <Route
                  path="/cotacao/:token"
                  element={
                    <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Carregando...</div>}>
                      <CotacaoPublicaPage />
                    </Suspense>
                  }
                />
                {/*
                  Sem token na URL (12/08/2026): a tela troca a barra de endereço para
                  /cotacao (replaceState, para o token não vazar em histórico/prints) e
                  guarda o token na sessão DA ABA — sem esta rota, o F5 do fornecedor caía
                  no catch-all e ia parar na tela de login do sistema.
                */}
                <Route
                  path="/cotacao"
                  element={
                    <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Carregando...</div>}>
                      <CotacaoPublicaPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/portal-cotacoes"
                  element={
                    <PrivateRoute
                      element={
                        <RoleGuard requiredRoles={papeisModuloNovo(['COMPRA', 'ADMIN', 'TI'], 'portal-cotacoes')}>
                          <PortalCotacoesList />
                        </RoleGuard>
                      }
                    />
                  }
                />
                <Route
                  path="/portal-cotacoes/aprovacoes"
                  element={
                    <PrivateRoute
                      element={
                        <RoleGuard requiredRoles={papeisModuloNovo(['COMPRA', 'ADMIN', 'TI'], 'portal-cotacoes')}>
                          <PortalCotacoesAprovacoes />
                        </RoleGuard>
                      }
                    />
                  }
                />
                {/*
                  Supervisão do fluxo (13/08/2026 — item 9 do backlog do Portal): leitura,
                  mesmos papéis das demais consultas do módulo. Declarada antes de `:id`
                  junto com as outras estáticas, seguindo o padrão do arquivo.
                */}
                <Route
                  path="/portal-cotacoes/supervisao"
                  element={
                    <PrivateRoute
                      element={
                        <RoleGuard requiredRoles={papeisModuloNovo(['COMPRA', 'ADMIN', 'TI'], 'portal-cotacoes')}>
                          <PortalCotacoesSupervisao />
                        </RoleGuard>
                      }
                    />
                  }
                />
                <Route
                  path="/portal-cotacoes/:id"
                  element={
                    <PrivateRoute
                      element={
                        <RoleGuard requiredRoles={papeisModuloNovo(['COMPRA', 'ADMIN', 'TI'], 'portal-cotacoes')}>
                          <PortalCotacaoDetalhe />
                        </RoleGuard>
                      }
                    />
                  }
                />
              </>
            )}

            <Route
              path={USERS_TABLE()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                      <UsersTablePage sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/* at-75 (ADR 0033): a única tela do papel RASTREADOR. */}
            <Route
              path={RASTREAMENTO()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['RASTREADOR', 'ADMIN', 'TI']}>
                      <RastreamentoPage />
                    </RoleGuard>
                  }
                />
              }
            />

            {/*
              at-114 (ADR 0059): Oficina Externa.

              A separação de ambientes que o Victor pediu (24/09/2026) é feita AQUI, por papel: o
              INSPETOR alcança só as duas primeiras rotas — a árvore Região > Oficina > Placa e a
              fila do que ainda não subiu —, e nenhuma tela da oficina interna. A terceira é a
              leitura de quem não vai à oficina (Chefe e setor de Eventos).
            */}
            <Route
              path={OFICINA_EXTERNA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['INSPETOR', 'ADMIN']}>
                      <OficinaExternaArvorePage />
                    </RoleGuard>
                  }
                />
              }
            />
            <Route
              path={OFICINA_EXTERNA_FILA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['INSPETOR', 'ADMIN']}>
                      <OficinaExternaFilaPage />
                    </RoleGuard>
                  }
                />
              }
            />
            <Route
              path={OFICINAS_EXTERNAS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CHEFE_DE_OFICINA', 'EVENTOS', 'ADMIN', 'TI']}>
                      <OficinasExternasLeituraPage />
                    </RoleGuard>
                  }
                />
              }
            />

            {/* at-85 (ADR 0039): a única tela do papel EVENTOS. */}
            <Route
              path={ANDAMENTO_VEICULOS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['EVENTOS', 'ADMIN', 'TI']}>
                      <AndamentoVeiculosPage />
                    </RoleGuard>
                  }
                />
              }
            />

            {/*
              at-86: Dashboard Oficina. SÓ ADMIN — o painel reúne valor protegido da carteira e
              arrecadação, e por isso o TI não entra, apesar de enxergar as telas de leitura da
              Oficina (ADR 0012): é a mesma razão pela qual ele não tem Contac.
            */}
            <Route
              path={DASHBOARD_ASSOCIACAO()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CONTAC', 'ADMIN']}>
                      <DashboardAssociacaoPage sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/*
              contac/at-11: Radar Financeiro — as recomendações proativas sobre o Contac.
              Mesmo par de papéis do painel da carteira: a superfície é a mesma base financeira.
            */}
            <Route
              path={RADAR_FINANCEIRO()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CONTAC', 'ADMIN']}>
                      <RadarFinanceiroPage />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={DASHBOARD_OFICINA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN']}>
                      <DashboardOficinaPage sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/*
              at-100 (ADR 0050): Dashboard de Compras. ADMIN e COMPRA — o TI fica de fora porque a
              tela cruza carga por comprador, que é produtividade de pessoa nomeada (mesma razão do
              Dashboard Oficina). O TI entra só na configuração dos de-paras, em
              /admin/sistema/compras.
            */}
            <Route
              path={DASHBOARD_COMPRAS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'GESTAO_COMPRAS']}>
                      <DashboardComprasPage sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={RECEPCAO()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['RECEPCAO', 'TI']}>
                      <Recepcao sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={CHEFE_DE_OFICINA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CHEFE_DE_OFICINA', 'TI']}>
                      <ChefeDeOficina sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/*
              Orçamento Cilia: as rotas só são registradas com a env do backend do módulo
              (ORCAMENTO_CILIA_HABILITADO), mesmo mecanismo do Portal de Cotações. Sem
              ela — o caso do build de produção, que injeta apenas REACT_APP_BACKEND_URL —
              o módulo não existe na UI, em vez de aparecer no menu e chamar `undefined/...`.
            */}
            {ORCAMENTO_CILIA_HABILITADO && (
              <>
                {/* CHEFE_DE_OFICINA entra pela aba "Autorizações" (30/07/2026 —
                    a tela própria de autorizações virou aba desta) */}
                <Route
                  path={ORCAMENTOS_CILIA()}
                  element={
                    <PrivateRoute
                      element={
                        <RoleGuard requiredRoles={papeisModuloNovo(['ORCAMENTO', 'CHEFE_DE_OFICINA', 'COMPRA', 'TI'], 'orcamento-cilia')}>
                          <OrcamentoCiliaList sidebarRef={sidebarRef} />
                        </RoleGuard>
                      }
                    />
                  }
                />

                <Route
                  path={ORCAMENTOS_CILIA_DETAIL()}
                  element={
                    <PrivateRoute
                      element={
                        <RoleGuard requiredRoles={papeisModuloNovo(['ORCAMENTO', 'COMPRA', 'TI'], 'orcamento-cilia')}>
                          <OrcamentoCiliaDetail />
                        </RoleGuard>
                      }
                    />
                  }
                />
              </>
            )}

            {/*
              Painel de Sincronização da Base (banco-de-dados-atual, o serviço que alimenta
              o Contac): mesmo mecanismo dos módulos satélites — a rota só existe com a env
              do backend do módulo (BANCO_SYNC_HABILITADO). Acesso: ADMIN e TI (decisão do
              Victor, 21/08/2026).
            */}
            {BANCO_SYNC_HABILITADO && (
              <Route
                path="/sincronizacao-base"
                element={
                  <PrivateRoute
                    element={
                      <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                        <SincronizacaoBasePage />
                      </RoleGuard>
                    }
                  />
                }
              />
            )}

            {/*
              Painel de Sincronizacao FIPE (fipe-atual): mesmo desenho do painel do banco —
              a rota so existe quando REACT_APP_FIPE_URL aponta para o backend do modulo
              (FIPE_SYNC_HABILITADO). Acesso: ADMIN e TI.
            */}
            {FIPE_SYNC_HABILITADO && (
              <Route
                path="/sincronizacao-fipe"
                element={
                  <PrivateRoute
                    element={
                      <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                        <SincronizacaoFipePage />
                      </RoleGuard>
                    }
                  />
                }
              />
            )}

            {/*
              Previsão de Peças (previsao-pecas/at-02): lê o backend do SOAC. Absorveu a antiga
              tela de Estoque (`/estoque`, removida) e deixou de depender de env para existir
              (decisão do Victor, 23/10/2026). O TI vê em leitura (24/10/2026).
              ⚠️ `papeisModuloNovo` FICA até o go-live (decisão do Victor, 24/10/2026): em
              produção, só o ADMIN a alcança até ele liberar com o responsável definido.
            */}
            <Route
              path={PREVISAO_PECAS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard
                      requiredRoles={papeisModuloNovo(['COMPRA', 'ESTOQUE', 'CHEFE_DE_OFICINA', 'ADMIN', 'TI'], 'previsao-pecas')}
                    >
                      <PrevisaoPecasPage />
                    </RoleGuard>
                  }
                />
              }
            />
            {/* A tela de UM pedido, com a chegada de cada peça — mesmos papéis da tela principal. */}
            <Route
              path={PREVISAO_PECAS_PEDIDO()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard
                      requiredRoles={papeisModuloNovo(['COMPRA', 'ESTOQUE', 'CHEFE_DE_OFICINA', 'ADMIN', 'TI'], 'previsao-pecas')}
                    >
                      <PedidoPage />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={PARTS_LOGS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CHEFE_DE_OFICINA', 'COMPRA', 'ESTOQUE', 'TI']}>
                      <PartLogs sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/*
              `ORCAMENTO` entrou no guard da Auditoria na at-05 do Cilia (16/08/2026): ele
              audita os orçamentos que confere. Dentro da tela, a aba de cada módulo tem o
              seu próprio gate — ele alcança a página, não o conteúdo dos outros.

              ⚠️ `EVENTOS` entrou na at-90 (decisão do Victor, 03/09/2026), porque a aba
              **Feedback** nasceu para ele — é emenda consciente ao ADR 0039, que lhe dava uma
              tela só. A partir da at-90 este guard governa só o ACESSO À PÁGINA: quais abas
              cada papel vê é decidido pela matriz configurável (`GET /auditoria-abas/minhas`),
              e a aba Feedback nasce sem papel nenhum. Consequência aceita: até o ADMIN
              conceder, o EVENTOS abre a Auditoria e lê "nenhuma área liberada para o seu
              perfil" — estado honesto, em vez de uma página em branco.
            */}
            <Route
              path={AUDITORIA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CHEFE_DE_OFICINA', 'COMPRA', 'ESTOQUE', 'ADMINISTRACAO', 'TI', 'ORCAMENTO', 'EVENTOS']}>
                      <AuditoriaPage sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={CONTROLE_PRODUCAO()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CHEFE_DE_OFICINA', 'COLABORADOR', 'TI']}>
                      <ControleProducao sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/* Compatibilidade com links antigos, anteriores ao at-31: a tela do colaborador
                passou a ser o Controle de Produção. Não existe mais tela em /colaboradores. */}
            <Route
              path="/colaboradores/:id"
              element={<Navigate to="/controle-producao" replace />}
            />

            {/*
              at-107: o CHEFE_DE_OFICINA entrou (decisão do Victor, 17/09/2026). Ele passou a
              poder gerar o Relatório de Veículos, e o botão vive nesta tela.

              Ele alcança a tela INTEIRA, não só o relatório. Antes de mexer aqui, as rotas que a
              tela consome foram conferidas contra o papel: busca, filtro, detalhe do veículo,
              histórico de etapas e as de checklist já o aceitavam; o histórico de colaboradores
              era ADMIN+TI e recebeu o papel no mesmo commit. O Controle de Liberação continua
              sendo da Administração (ADR 0015) e o botão não aparece para ele.

              ⚠️ Comentário de bloco NUNCA logo depois de `element={`: o extrator do contrato
              (`papeis-da-tela.spec.ts`) limpa comentário entre chaves com um casamento
              preguiçoso, e a abertura de `element={` casa como se fosse a chave dele — engolindo
              tudo até o próximo fecha-comentário seguido de chave, e levando embora os RoleGuards
              do caminho. Medido em 17/09/2026: 47 guards achados caíram para 44, e três telas
              viraram órfãs na matriz. (Escrever a sequência de fechamento aqui dentro também
              quebra este comentário — foi o que aconteceu na primeira tentativa.)
            */}
            <Route
              path={TELA_DE_CONTROLE()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'TI', 'CHEFE_DE_OFICINA']}>
                      <TelaDeControle sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path="/checklists-realizados"
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['RECEPCAO', 'CHEFE_DE_OFICINA', 'TI']}>
                      <ChecklistHistoryPage sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={ADMINISTRACAO()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMINISTRACAO', 'ADMIN', 'TI']}>
                      <Administracao sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/*
              Administração do Sistema por MÓDULO (at-73). `/admin/sistema` abre o hub
              de seleção; cada módulo tem URL própria e o seu RoleGuard — a config da
              Oficina é ADMIN/TI, a de Cotações é ADMIN/COMPRA (+TI). Quem só alcança
              um módulo é redirecionado direto pelo hub, sem clique a mais.
            */}
            <Route
              path={ADMIN_SISTEMA()}
              element={
                <PrivateRoute
                  element={
                    // COMPRA só está aqui para alcançar a config de Cotações — com a
                    // flag de teste (at-74), esse motivo some e o papel sai junto.
                    // ADMIN/TI ficam: a config da Oficina não é módulo novo.
                    <RoleGuard requiredRoles={modulosNovosSomenteAdmin() ? ['ADMIN', 'TI'] : ['ADMIN', 'TI', 'COMPRA']}>
                      <AdminSistemaHub />
                    </RoleGuard>
                  }
                />
              }
            />
            <Route
              path={ADMIN_SISTEMA_OFICINA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                      <AdministracaoSistemaPage sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />
            {/*
              Logs do sistema (transversal/at-91, ADR 0062): tudo o que aconteceu na aplicação.
              Sem gate por env — o backend é o próprio SOAC. ADMIN e TI (decisão do Victor).
            */}
            <Route
              path={ADMIN_SISTEMA_LOGS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                      <LogsDoSistemaPage />
                    </RoleGuard>
                  }
                />
              }
            />
            {/*
              Consumo de IA (at-12): tokens e custo estimado das chamadas de IA. Sem gate por
              env — o backend é o próprio SOAC; sem a chave da Anthropic a tela mostra zero
              consumo, que é resposta legítima.
            */}
            <Route
              path={ADMIN_SISTEMA_CONSUMO_IA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                      <ConsumoIaPage />
                    </RoleGuard>
                  }
                />
              }
            />
            {/*
              Radar (at-82, ADR 0046): como a equipe está avaliando os achados do assistente, em
              cada radar, e o histórico das análises. Sem gate por env — o backend é o próprio
              SOAC; sem a chave da Anthropic a tela mostra o estado vazio honesto.

              🔴 at-83: ADMIN **e TI**, e a razão da mudança precisa ficar clara. Até aqui era só
              ADMIN porque o CONTEÚDO da tela carrega os títulos e números dos achados, e o Radar
              Financeiro é feito de valores do Contac, que o ADR 0012 mantém fora do alcance do TI
              — isso continua valendo. O que mudou foi a tela ganhar a aba **Ativação** (ligar e
              desligar cada Radar), que é operação de sistema e não mostra número nenhum.

              A aba de achados NÃO é montada para quem não é ADMIN, e as rotas dela
              (`/radar/admin/avaliacao` e `/radar/admin/exploracoes`) seguem `@Roles(ADMIN)` no
              backend — é ali que está a garantia, não aqui.
            */}
            <Route
              path={ADMIN_SISTEMA_RADAR()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                      <RadarAdminPage />
                    </RoleGuard>
                  }
                />
              }
            />
            {/*
              at-100: os de-paras do Dashboard de Compras. Aqui o TI ENTRA, ao contrário do painel:
              são cadastros (código do ERP -> nome, grafia -> oficina, local -> setor) e não há
              número de produtividade nenhum nesta superfície.
            */}
            <Route
              path={ADMIN_SISTEMA_COMPRAS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                      <ComprasConfigPage />
                    </RoleGuard>
                  }
                />
              }
            />
            {/*
              at-114: os cadastros da Oficina Externa (regiões, oficinas, etapas, checklist da
              visita, parâmetros). O TI ENTRA — é configuração de sistema (ADR 0012), do mesmo
              tipo que ele já faz com esteiras, tags e templates de checklist. As quatro ações do
              INSPETOR ficam de fora, no controller separado.
            */}
            <Route
              path={ADMIN_SISTEMA_OFICINA_EXTERNA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                      <OficinaExternaConfigPage />
                    </RoleGuard>
                  }
                />
              }
            />
            {/*
              Grupos de Acesso (at-81, ADR 0044): conjuntos NOMEADOS de perfis, com tela inicial
              própria. Sem gate por env — o backend é o próprio SOAC.
            */}
            <Route
              path={ADMIN_SISTEMA_GRUPOS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'TI']}>
                      <GruposDeAcessoPage />
                    </RoleGuard>
                  }
                />
              }
            />
            {/*
              Config de Comissionamentos (comissionamentos/at-01). Sem gate por env: o carimbo e o
              status vêm do backend do SOAC; só o disparo manual depende da env do painel de
              sincronização, e ele degrada sozinho (botão desabilitado com explicação).

              ⚠️ SEM TI, ao contrário de todos os outros cartões do hub: o ADR 0012 mantém
              Comissionamentos fora do alcance dele. Mexer nisto significa mexer no ADR.
            */}
            <Route
              path={ADMIN_SISTEMA_COMISSIONAMENTOS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['ADMIN', 'COMISSIONAMENTO']}>
                      <ComissionamentosConfigPage />
                    </RoleGuard>
                  }
                />
              }
            />
            {/*
              Contac (contac/at-13, ADR 0007 do Contac): a régua de cores do Dashboard de Placas e
              as regras do Radar do Relacionamento, explicadas para apresentação. Só leitura.

              ⚠️ SEM TI, como Comissionamentos: o Contac é financeiro e o ADR 0012 o mantém fora
              do alcance dele. O CONTAC entra por ser o dono do módulo.
            */}
            <Route
              path={ADMIN_SISTEMA_CONTAC()}
              element={
                <PrivateRoute
                  element={
                    // 🔴 EMBARGO da contac/at-18: era ['ADMIN', 'CONTAC']. Metade desta tela
                    // descreve o Radar do Relacionamento, que não pode ficar visível ao usuário
                    // final antes da apresentação à diretoria. O CONTAC não perde nada que já
                    // usasse: a tela nasceu na at-13 e nunca chegou a produção.
                    <RoleGuard requiredRoles={['ADMIN']}>
                      <ContacConfigPage />
                    </RoleGuard>
                  }
                />
              }
            />
            {PORTAL_COTACOES_HABILITADO && (
              <Route
                path={ADMIN_SISTEMA_COTACOES()}
                element={
                  <PrivateRoute
                    element={
                      <RoleGuard requiredRoles={papeisModuloNovo(['COMPRA', 'ADMIN', 'TI'], 'portal-cotacoes')}>
                        <PortalCotacoesConfig />
                      </RoleGuard>
                    }
                  />
                }
              />
            )}
            {/*
              Orçamento Cilia (12/08/2026): mesma mudança que o Portal recebeu na at-73 —
              a configuração do módulo saiu de dentro da tela de orçamentos e virou um
              módulo daqui. O gate por env vale para ela também: sem o backend do Cilia,
              nem a rota existe (o cartão do hub também desaparece).
            */}
            {ORCAMENTO_CILIA_HABILITADO && (
              <Route
                path={ADMIN_SISTEMA_CILIA()}
                element={
                  <PrivateRoute
                    element={
                      <RoleGuard requiredRoles={papeisModuloNovo(['ADMIN', 'TI'], 'orcamento-cilia')}>
                        <OrcamentoCiliaConfig />
                      </RoleGuard>
                    }
                  />
                }
              />
            )}
            {/*
              Integrações com plataformas externas de mensagem (at-07). Módulo próprio porque
              integração é transversal — o mesmo OctaDesk atende convite de cotação agora e,
              na entrega 2, checklist e pesquisa de satisfação.

              ⚠️ O gate por env é do PORTAL nesta entrega, e é honesto: a configuração vive no
              backend dele, então sem a env a tela não teria a quem perguntar. Na entrega 2,
              quando o SOAC entrar, o gate sai (e o cartão do hub deixa de ser condicional).
            */}
            {PORTAL_COTACOES_HABILITADO && (
              <Route
                path={ADMIN_SISTEMA_INTEGRACOES()}
                element={
                  <PrivateRoute
                    element={
                      <RoleGuard requiredRoles={papeisModuloNovo(['ADMIN', 'TI'])}>
                        <IntegracoesPage />
                      </RoleGuard>
                    }
                  />
                }
              />
            )}

            <Route
              path={CONTAC_DASHBOARD()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CONSULTOR', 'REGIONAL', 'CONTAC']}>
                      <ContacDashboard sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={CONTAC_DASHBOARD_CONSULTOR()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CONSULTOR', 'CONTAC']}>
                      <ContacDashboardConsultor sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={CONTAC_DASHBOARD_REGIONAL()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['REGIONAL', 'CONTAC']}>
                      <DashboardRegional sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/* [FUTURE-REFATORACAO: REMOVE_COOPERATIVAS]
            <Route
              path={CONTAC_DASHBOARD_COOPERATIVA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CONTAC']}>
                      <DashboardCooperativa sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />
            */}

            <Route
              path={RESULTADO_POR_PLACA()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CONSULTOR', 'REGIONAL', 'RELACIONAMENTO', 'CONTAC']}>
                      <ResultadoPorPlaca sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            {/* <Route
              path={CONTAC_RELATORIO()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['CONSULTOR', 'REGIONAL', 'CONTAC']}>
                      <RelatorioPage sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            /> */}



            <Route
              path={FORMULARIOS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard
                      requiredRoles={[
                        'RECEPCAO',
                        'CHEFE_DE_OFICINA',
                        'ORCAMENTO',
                        'ESTOQUE',
                        'COMPRA',
                        'COLABORADOR',
                        'CONSULTOR',
                        'REGIONAL',
                        'COMISSIONAMENTO',
                        'TI',
                      ]}
                    >
                      <FormulariosPage />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={COMISSIONAMENTOS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['COMISSIONAMENTO']}>
                      <ComissionamentosHome />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={COMISSIONAMENTOS_LISTAS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['COMISSIONAMENTO']}>
                      <Comissionamentos sidebarRef={sidebarRef} />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={COMISSIONAMENTOS_HOME()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['COMISSIONAMENTO']}>
                      <ComissionamentosHome />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={COMISSIONAMENTOS_REGRAS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['COMISSIONAMENTO']}>
                      <RegrasPage />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={COMISSIONAMENTOS_PAGAMENTOS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['COMISSIONAMENTO']}>
                      <PagamentosPage />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={COMISSIONAMENTOS_CONTRATOS()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['COMISSIONAMENTO']}>
                      <ContratosPage />
                    </RoleGuard>
                  }
                />
              }
            />

            <Route
              path={COMISSIONAMENTOS_MOVIMENTACOES()}
              element={
                <PrivateRoute
                  element={
                    <RoleGuard requiredRoles={['COMISSIONAMENTO']}>
                      <MovimentacoesRetroativasPage />
                    </RoleGuard>
                  }
                />
              }
            />
          </Routes>
        </div>
      </div>

      {accessToken && mustChangePassword && !isPasswordChangeDeferred && (
        <ModalTrocaSenhaPrimeiroAcesso onDefer={() => setIsPasswordChangeDeferred(true)} />
      )}
    </div>
  );
};

const AppWrapper: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Router>
          <App />
        </Router>
      </Provider>

      {process.env.NODE_ENV === 'development' ? (
        <ReactQueryDevtools initialIsOpen={false} />
      ) : null}
    </QueryClientProvider>
  );
};

export default AppWrapper;
